// Office desktop shell: a collapsible left sidebar with brand + grouped nav
// sections, and a main content area that flexes to fill the rest. The sidebar
// collapses to an icon rail (manual toggle, and collapsed by default on narrow
// screens) and scrolls when it grows, so new sections bolt on without rework.
//
// When expanded, the Director can drag-reorder the nav items WITHIN a section (a drag
// handle appears on hover). The order persists per user (GET/PUT /api/me/nav-order) so
// it survives a reload and follows the user across devices; "Reset to default order"
// clears it. The custom order is a presentation layer over OFFICE_NAV (applyNavOrder):
// it never adds, drops or moves an item between sections, so every route stays reachable.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAuth } from "../../lib/auth";
import { registerPushNavigationHandler } from "../../lib/pushClient";
import {
  OFFICE_NAV,
  applyNavOrder,
  applyNavLayout,
  flattenNavLayout,
  applyNavDragEnd,
  navGroupSortId,
  isSectionOpen,
  toggleGroup,
  officeTabTitle,
  activeSectionLabel,
  hubForLocation,
  navItemActive,
  navItemBadge,
  visibleTabsForRole,
  reconcileExpandedOnNavigate,
  coerceExpandedGroups,
  visibleNavForRole,
  groupBadgeTotal,
  type NavItem,
  type NavSection,
} from "../../lib/nav";
import { loadNavExpanded, saveNavExpanded, clearNavExpanded, hasStoredNavExpanded } from "../../lib/navExpanded";
import { navIconFor } from "../../lib/navIcons";
import { PinDragContext, pinDragSourceProps, usePinDrag } from "../../lib/pinDrag";
import { api } from "../../lib/api";
import { GlobalSearch } from "../../components/GlobalSearch";
import { HubTabs } from "../../components/HubTabs";
import { lockedLinkProps, useIsNavigationLocked } from "../../lib/navigationLock";
import { ThemeToggle } from "../../components/ThemeToggle";
import { DocumentsDrawerLinks, DocumentsMenu } from "../../components/DocumentsMenu";
import { PhoneViewButton } from "../../components/phoneView/PhoneViewButton";
import { PhoneViewHost } from "../../components/phoneView/PhoneViewStage";
import { PushNotificationsButton } from "../../components/PushNotificationsButton";
import { ConfirmButton } from "../../components/ConfirmButton";
// Session ASB review (F4): the warning before the 60-minute idle sign-out, mounted once for the whole office shell.
import { IdleSessionWarning } from "./IdleSessionWarning";

function initialCollapsed(): boolean {
  // Collapse by default on narrow screens (guard for SSR / no matchMedia).
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(max-width: 1024px)").matches;
}

// Session ACG locked colour logic (supersedes the Session AAV purple-bar active); the
// coloured left-border was REMOVED in Session ACH (the Director extended the app-wide
// no-left-stripe ban to nav items - no carve-out). GREEN = "you are here" (the active item:
// green text on a soft green tint, no border); PURPLE = navigation intent (the hover state:
// #a060e0 text on a soft purple tint, no border); an inactive item is a quiet brightened
// grey. The active/hover state is signalled ENTIRELY by background wash + text colour; the
// nav-icon chip border also greens on the active item. Tailwind alpha modifiers cannot
// synthesise from the CSS-var brand tokens, hence the arbitrary rgba values (the locked
// spec's exact figures). Session AFK: the inactive grey became --ele-nav-muted, its own token
// so the exact #8a8a8a is preserved on the dark theme and darkened for the light one; the two
// brand washes and the #a060e0 hover are brand colours and are identical in both themes.
const navLinkClass = (collapsed: boolean) => ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-ele px-2 py-1.5 text-xs transition ${
    isActive
      ? "bg-[rgba(99,196,31,0.07)] text-ele-green"
      : "text-[var(--ele-nav-muted)] hover:bg-[rgba(125,33,199,0.06)] hover:text-ele-nav-hover"
  } ${collapsed ? "justify-center" : ""}`;

function NavIcon({ icon, to, active = false }: { icon: string; to: string; active?: boolean }) {
  // In the collapsed icon rail the chip is the only visible element, so the active page's
  // chip carries the green "you are here" treatment (Session ACG; was the purple fill).
  // Session AGZ (Director decision): the chip now carries a lucide icon per route
  // (navIconFor), superseding the AFA two-letter glyph as the RENDERED mark; the glyph
  // string stays on the nav data as the fallback for any unmapped route, so a new nav
  // item without a mapping still shows something legible. The icon inherits currentColor,
  // so the locked green-active / muted-inactive link treatment colours it for free.
  const Icon = navIconFor(to);
  return (
    <span
      aria-hidden
      className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded border font-menu text-[10px] tracking-wide ${
        active ? "border-ele-green bg-[rgba(99,196,31,0.12)] text-ele-green" : "border-ele-purple-accent"
      }`}
    >
      {Icon ? <Icon size={14} aria-hidden /> : icon}
    </span>
  );
}

// A small chevron for a collapsible group heading: a right-pointing triangle that rotates to
// point down when the group is open. Reuses inline SVG (no new icon library, house rule).
function GroupChevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      className={`h-3 w-3 flex-none fill-current transition-transform duration-200 ${open ? "rotate-90" : ""}`}
    >
      <path d="M6 4l4 4-4 4z" />
    </svg>
  );
}

export function OfficeLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // Desktop push notifications (Session AFF): when an office window is already open, a
  // notification click focuses it and the service worker posts a navigate hint; without this
  // listener the window would focus but never move to e.g. /office/timesheets or (Session AKF)
  // /office, the dashboard MESSAGE_PUSH_TO_OFFICE now points at instead of the retired /office/
  // messages.
  useEffect(() => registerPushNavigationHandler((path) => navigate(path)), [navigate]);
  // Session AQS, item 18: a distinct browser tab title per page, so a dozen open office tabs are
  // tellable apart. Every page shared index.html's static "ELE-Guvnor" until now.
  useEffect(() => {
    document.title = `${officeTabTitle(pathname)} · ELE-Guvnor`;
  }, [pathname]);
  const [collapsed, setCollapsed] = useState<boolean>(initialCollapsed);
  // Inert menu links while a page holds the navigation lock (the Release and send run).
  const navLocked = useIsNavigationLocked();
  // Mobile nav drawer (Session ACW): below md there is NO permanent sidebar at all -- the
  // header hamburger opens this full-height overlay drawer instead, carrying the same
  // expanded nav. Plain component state, default closed, never persisted anywhere.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sections, setSections] = useState<NavSection[]>(OFFICE_NAV);
  // P-1, Session AQT: what is actually RENDERED to this login. Drag-reorder, the active-section
  // lookup and the auto-open effect all keep reading the full `sections` (a role restriction
  // hides a link, it does not delete data or change what a route resolves to); only the two
  // render sites below (the expanded drag list and the collapsed icon rail) read this instead.
  const visibleSections = useMemo(() => visibleNavForRole(sections, user?.role), [sections, user?.role]);
  // The set of nav-group labels the user has expanded (D28, Session AQT: auto-open the active
  // group and remember open groups per user, reusing the dormant server half -- User.navGroups /
  // setExpandedGroups -- built 25 June 2026 and left uncalled since 29 June). This SUPERSEDES the
  // Session AI Item 8 "always collapsed on login, sessionStorage only" behaviour: sessionStorage
  // is now only the synchronous fast-path for first paint (below, and cleared on sign-out so a
  // different login on a shared device never briefly shows the previous user's groups); the
  // server value, once loaded, is the one that persists a user's open groups across logins. The
  // user can still collapse any section, including the one they are in -- auto-open ADDS the
  // newly-active section to the set on arrival, it never force-pins it open (that was the
  // "Portfolio cannot collapse" bug the 29 June reversal fixed, and this must not reintroduce it).
  // SC7A: the calm menu has four groups and fourteen items, so every group starts OPEN (nothing hidden
  // behind a press) until the person closes one; a stored choice for THESE groups wins. Stored labels of
  // the old seven groups are not groups any more, so they are dropped (coerceExpandedGroups) and the
  // default applies.
  // A stored EMPTY list is a real choice (the person closed every group) and stays closed; only "nothing stored" and a list of
  // stale old-group labels fall back to the all-open default.
  const closedOnPurpose = useRef(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    if (!hasStoredNavExpanded()) return new Set(OFFICE_NAV.map((s) => s.label));
    const raw = [...loadNavExpanded()];
    if (raw.length === 0) {
      closedOnPurpose.current = true;
      return new Set();
    }
    const stored = coerceExpandedGroups(raw, OFFICE_NAV);
    return stored.length > 0 ? new Set(stored) : new Set(OFFICE_NAV.map((s) => s.label));
  });
  // The active route's own section, tracked so reconcileExpandedOnNavigate can tell a genuine
  // section change (auto-open the new one) from navigating within the same section (no-op).
  // Seeded null, NOT the current section: reconcileExpandedOnNavigate treats null -> X as a
  // transition too, which is what makes the FIRST render's own auto-open effect below fire for
  // "on first load, the active group is open" -- seeding it with the current section would make
  // the initial arrival look like "no change happened" and silently skip auto-opening it.
  const prevActiveSection = useRef<string | null>(null);
  // Live count of active projects, shown as a badge on the Projects nav item. Best-effort:
  // a failure simply hides the badge (0).
  const [projectCount, setProjectCount] = useState(0);
  // True once the user has reordered/reset, so the in-flight load GET (which carries the
  // pre-drag order) does not race-clobber a just-completed drag.
  const interacted = useRef(false);
  // A save of the nav layout failed (drag-reorder or reset). The UI keeps the order the user set;
  // this surfaces a quiet notice so the failure is not silent (Session AEH, FIX-2 B3).
  const [navSaveError, setNavSaveError] = useState(false);
  // Focus plumbing for the drawer's aria-modal contract (Session ACW review fix): focus moves
  // onto the panel when the drawer opens and back onto the hamburger when it closes.
  const drawerPanelRef = useRef<HTMLDivElement | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  // Whether the dashboard is in customise mode (Session AEA). Published into PinDragContext by
  // OfficeDashboard (the other side of the Outlet) so the nav links can become pin-drag sources
  // exactly while the office is arranging the dashboard, and at no other time.
  const [pinCustomising, setPinCustomising] = useState(false);
  const pinDragValue = useMemo(
    () => ({ customising: pinCustomising, setCustomising: setPinCustomising }),
    [pinCustomising],
  );

  // A small drag distance before activating, so a click on the handle (or a NavLink)
  // is never read as a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Load the user's saved item ORDER once and apply it (defaults on any failure), and the same
  // request's expandedGroups (D28, Session AQT): the two have always travelled together on this
  // one endpoint, so one round trip restores both.
  useEffect(() => {
    let live = true;
    api.preferences
      .getNavOrder()
      .then(({ order, layout, expandedGroups }) => {
        if (!live) return;
        if (!interacted.current) {
          // Prefer the structured layout (Session AD, Item 4: cross-group order); fall back to the
          // legacy flat within-section order for a row saved before Item 4, else the default.
          setSections(layout && layout.length > 0 ? applyNavLayout(OFFICE_NAV, layout) : applyNavOrder(OFFICE_NAV, order));
        }
        // A server value replaces the sessionStorage fast-path once it arrives; a renamed/removed
        // group in a stored row is dropped rather than crashing on an unknown label. No stored
        // value (a first login, or an older row with none) leaves the active-group auto-open
        // below as the only thing that opens anything, matching "on first load only the active
        // group is open".
        if (Array.isArray(expandedGroups) && expandedGroups.length === 0) {
          // The person closed every group: that is their choice, not "nothing stored".
          closedOnPurpose.current = true;
          setExpanded(new Set());
          saveNavExpanded(new Set());
        } else {
          const fromServer = coerceExpandedGroups(expandedGroups, OFFICE_NAV);
          if (fromServer.length > 0) {
            const next = new Set(fromServer);
            setExpanded(next);
            saveNavExpanded(next);
          }
        }
      })
      .catch(() => {
        /* best-effort: keep the default order and the sessionStorage-seeded groups */
      });
    return () => {
      live = false;
    };
  }, []);

  // Auto-open the section the user has just navigated into (D28): ADDS it to the expanded set,
  // never force-pins it -- the user can still collapse it afterward. reconcileExpandedOnNavigate
  // no-ops on navigating within the same section or into one already expanded, so this never
  // fights a deliberate collapse.
  useEffect(() => {
    const nextActive = activeSectionLabel(sections, pathname, search);
    // First load with every group closed on purpose: do not re-open the active one behind the person's back.
    const skipFirst = closedOnPurpose.current && prevActiveSection.current === null;
    const { next, changed } = skipFirst ? { next: expanded, changed: false } : reconcileExpandedOnNavigate(expanded, prevActiveSection.current, nextActive);
    prevActiveSection.current = nextActive;
    if (changed) {
      setExpanded(next);
      saveNavExpanded(next);
      void api.preferences.setExpandedGroups([...next]).catch(() => {
        /* best-effort: the open state still works locally this session */
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, sections]);

  // Active-project count for the Projects nav badge (best-effort; 0 hides the badge).
  useEffect(() => {
    let live = true;
    api.projects
      .activeCount()
      .then(({ count }) => {
        if (live) setProjectCount(count);
      })
      .catch(() => {
        /* best-effort: no badge */
      });
    return () => {
      live = false;
    };
  }, []);

  // Close the drawer on ANY route navigation (Session ACW). Watching the pathname is the
  // robust route: it catches a NavLink click inside the drawer, a global-search selection
  // and a programmatic navigate alike, without forking the shared nav rendering to thread
  // an onClick through every link.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Lock body scroll while the drawer is open. The cleanup runs on close AND on unmount,
  // so a layout teardown mid-open can never leave the page unscrollable.
  useEffect(() => {
    if (!drawerOpen) return;
    document.body.classList.add("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [drawerOpen]);

  // If the viewport crosses to >= md while the drawer is open (a phone rotating to
  // landscape-tablet width), the drawer and its hamburger both go CSS-hidden -- without
  // this, the body scroll lock would be stranded with no visible control to release it.
  // Guarded for SSR / no matchMedia exactly like initialCollapsed (jsdom has neither).
  useEffect(() => {
    if (!drawerOpen) return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 768px)");
    if (mq.matches) {
      setDrawerOpen(false);
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) setDrawerOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [drawerOpen]);

  // Fulfil the aria-modal contract the drawer declares (Session ACW review fix): focus moves
  // INTO the dialog on open, Escape closes it (the platform's modal convention -- see
  // ConfirmDeleteModal), and focus returns to the hamburger on close. Together with the
  // `inert` attribute the <main> sibling carries while the drawer is open (below), background
  // content genuinely leaves the tab order and the accessibility tree, so Tab cycles within
  // the drawer without a hand-rolled trap. The cleanup's optional chains make the restore a
  // safe no-op when the layout itself unmounts mid-open (e.g. sign-out).
  useEffect(() => {
    if (!drawerOpen) return;
    drawerPanelRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      hamburgerRef.current?.focus();
    };
  }, [drawerOpen]);

  // The Messages nav badge (Session ACZ, api.messages.unreadCount()) was retired with the Messages
  // nav item in Session AKF: the office route is gone, so a badge counting unread threads nobody
  // can open from here would be pointless best-effort work fetched on every navigation for nothing
  // ever shown. The underlying endpoint is untouched; this was its only caller.
  const badges: Record<string, number> = {
    ...(projectCount > 0 ? { "/office/projects": projectCount } : {}),
  };

  async function signOut() {
    // Clear only the LOCAL sessionStorage fast-path, so a different login on a shared device never
    // briefly shows this user's groups before the server value (D28) loads. The next login's real
    // open groups are restored from the server, not reset to collapsed.
    clearNavExpanded();
    await logout();
    navigate("/login", { replace: true });
  }

  // Fully-flexible drag (Session AD, Item 4): an item can move to any position in any group, and a
  // group heading can move the whole group. The pure applyNavDragEnd resolves the drop (an item id is
  // its `to` path, a group id is navGroupSortId(label)); we persist the structured layout. A save
  // failure leaves the UI as the user set it.
  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over) return;
    const { sections: next, changed } = applyNavDragEnd(sections, String(active.id), String(over.id));
    if (!changed) return;
    interacted.current = true;
    setSections(next);
    setNavSaveError(false);
    void api.preferences.setNavLayout(flattenNavLayout(next)).catch(() => setNavSaveError(true));
  }

  async function resetOrder() {
    interacted.current = true;
    setSections(OFFICE_NAV);
    setNavSaveError(false);
    await api.preferences.setNavLayout([]).catch(() => setNavSaveError(true));
  }

  // Toggle a group's expanded state: sessionStorage for this tab's instant re-render, and the
  // server (D28) so it is remembered for the user's next login. Any section can be collapsed,
  // including the one containing the active route.
  function toggleSection(label: string) {
    const next = toggleGroup(expanded, label);
    setExpanded(next);
    saveNavExpanded(next);
    void api.preferences.setExpandedGroups([...next]).catch(() => {
      /* best-effort: the open state still works locally this session */
    });
  }

  // The full EXPANDED nav (drag-to-reorder groups + the reset button), shared verbatim by
  // the desktop sidebar and the mobile drawer (Session ACW) so the two surfaces can never
  // drift apart. Each render is its own independent DndContext instance, but the state it
  // reads and writes (sections, expanded, the persistence handlers) lives here on
  // OfficeLayout, so a reorder or group toggle made in the drawer is the same one the
  // desktop sidebar shows.
  const expandedNav = (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      {/* Two levels of sortable (Session AD, Item 4): the group HEADINGS reorder whole
          groups, and the items in each group reorder AND move across groups. A single
          DndContext, so a drop is resolved against any group heading or item. */}
      <SortableContext items={visibleSections.map((s) => navGroupSortId(s.label))} strategy={verticalListSortingStrategy}>
        {visibleSections.map((section) => (
          <SortableGroup
            key={section.label}
            section={section}
            open={isSectionOpen(section.label, expanded)}
            onToggle={() => toggleSection(section.label)}
            badges={badges}
          />
        ))}
      </SortableContext>
      <ConfirmButton
        onConfirm={resetOrder}
        label="Reset to default order"
        confirmLabel="Reset the order?"
        className="mt-2 px-2 font-menu text-[10px] tracking-wider text-ele-body underline opacity-60 transition hover:opacity-100"
      />
      {navSaveError && (
        <p role="alert" className="mt-2 px-2 font-menu text-[10px] text-ele-danger">
          Could not save the menu order. It is applied for now. Please try again.
        </p>
      )}
    </DndContext>
  );

  // Identity + sign out, shared by the sidebar footer (collapsed = the icon-rail "Exit"
  // form) and the mobile drawer footer (always the expanded form).
  const identityFooter = (rail: boolean) => (
    <div className="border-t border-ele-purple-accent px-3 py-3 text-sm">
      {!rail && <div className="mb-1 truncate text-ele-body">{user?.displayName}</div>}
      {!rail && (
        <Link to="/office/account" className="mb-1 block text-ele-body hover:text-ele-green hover:underline">
          My account
        </Link>
      )}
      {/* The office <-> engineer switcher: only for a login that actually HAS an engineer record of
          its own (User.hasEngineerRecord, from GET /api/auth/me, Session AGW). Today that is the
          Director's admin login alone (linked to TECH-002 by the AQP migration), but the gate is the
          fact, not the address, so it extends itself the moment any other admin/office login is ever
          linked the same way -- no second place to update. A login with no engineer record gets no
          link: it would only land them on OversightNotice's "you have none of your own" panel. */}
      {!rail && user?.hasEngineerRecord && (
        <Link to="/engineer" title="Switch to the engineer app" className="mb-1 block text-ele-body hover:text-ele-green hover:underline">
          Engineer view
        </Link>
      )}
      <button onClick={signOut} title="Sign out" className="text-ele-green hover:underline">
        {rail ? "Exit" : "Sign out"}
      </button>
    </div>
  );

  // SC7A: the tab strip of the hub the address belongs to, drawn only on a tab's own page (never on a record
  // under it) and only when the hub holds more than one page this login may see.
  const hubStrip = (() => {
    const hub = hubForLocation(sections, pathname, search);
    if (!hub || !hub.atRoot || hub.tabs.length < 2) return null;
    const visible = visibleTabsForRole(hub.item, user?.role);
    if (visible.length < 2) return null;
    const active = hub.tabs[hub.tabIndex];
    const idx = visible.findIndex((t) => t === active);
    if (idx === -1) return null;
    return <HubTabs label={hub.item.label} tabs={visible} activeIndex={idx} badges={badges} />;
  })();

  return (
    // .office-ui scopes the Session ACG density/design system (13-14px base, quiet hairline
    // chrome) to the office shell only -- the engineer PWA and public pages never carry it.
    <PinDragContext.Provider value={pinDragValue}>
    <div className="office-ui flex min-h-dvh bg-ele-base text-ele-body">
      {/* The permanent sidebar is a DESKTOP surface: hidden entirely below md (Session ACW),
          where the hamburger-opened drawer below takes over and the content gets the full
          width. At >= md everything here (collapse toggle, icon rail, drag-reorder) is
          exactly as it was. */}
      <aside
        className={`sticky top-0 hidden h-dvh flex-col border-r border-ele-purple-accent bg-[var(--ele-shell)] transition-[width] duration-150 md:flex ${
          collapsed ? "w-16" : "w-64"
        }`}
      >
        {/* Collapse toggle only (Session ACG): the single brand mark now lives in the 48px
            top header, so the sidebar no longer duplicates the logo. The row is h-12 so its
            bottom hairline aligns with the header's gradient rule. */}
        <div className="flex h-12 flex-none items-center justify-end border-b border-ele-purple-accent px-2">
          <button
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
            className="rounded-ele border border-ele-purple-accent px-2 py-1 text-ele-green hover:bg-ele-inset"
          >
            {collapsed ? "»" : "«"}
          </button>
        </div>

        {/* Grouped nav (scrolls when it grows). Collapsed = a plain icon rail; expanded
            = drag-to-reorder within each section (the shared expandedNav). */}
        <nav className="ele-sidebar-scroll flex-1 overflow-y-scroll px-2 py-3">
          {collapsed ? (
            visibleSections.map((section) => (
              <div key={section.label} className="mb-4">
                <ul className="space-y-0.5">
                  {section.items.map((item) => (
                    <li key={item.to} className="relative">
                      {/* The rail icon is a pin-drag source too while the dashboard is
                          customising (Session AEA) -- the spread overrides the title with the
                          drag hint for that mode only. */}
                      <Link
                        to={item.to}
                        title={item.hint ? `${item.label}: ${item.hint}` : item.label}
                        aria-current={navItemActive(item, pathname, search) ? "page" : undefined}
                        className={navLinkClass(true)({ isActive: navItemActive(item, pathname, search) })}
                        {...pinDragSourceProps(item, pinCustomising)}
                        {...lockedLinkProps(navLocked)}
                      >
                        <NavIcon icon={item.icon} to={item.to} active={navItemActive(item, pathname, search)} />
                      </Link>
                      {navItemBadge(item, badges) ? (
                        <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-ele-green px-1 text-[9px] font-bold text-ele-bg" aria-hidden>
                          {navItemBadge(item, badges)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            expandedNav
          )}
        </nav>

        {/* Documents (Director, 27 October 2026): the same links the phone drawer carries, at the foot of the sidebar,
            so the PC has them in the same place. Hidden in the collapsed icon rail, where the header icon still has them. */}
        {collapsed ? null : <DocumentsDrawerLinks role={user?.role} testId="sidebar-documents" />}

        {/* Identity + sign out */}
        {identityFooter(collapsed)}
      </aside>

      {/* Mobile nav drawer (Session ACW): the hamburger in the header opens this full-height
          overlay -- the SAME expanded nav (drag-to-reorder, sessionStorage expanded-group
          state, reset, identity + sign out) sliding in from the left over a dark backdrop.
          Tapping the backdrop closes it; any route navigation closes it (the pathname effect
          above); body scroll is locked while open. z-40 sits above the sticky header (z-20)
          and the search dropdown (z-30); md:hidden keeps it a mobile-only surface even if
          the open state were somehow set on a desktop viewport. */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            aria-hidden
            data-testid="drawer-backdrop"
            onClick={() => setDrawerOpen(false)}
            className="ele-drawer-backdrop absolute inset-0 bg-black/60"
          />
          <div
            ref={drawerPanelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="ele-drawer-panel absolute left-0 top-0 flex h-dvh w-72 max-w-[85vw] flex-col border-r border-ele-purple-accent bg-[var(--ele-shell)] outline-none"
          >
            <nav className="ele-sidebar-scroll flex-1 overflow-y-scroll px-2 py-3">{expandedNav}</nav>
            <DocumentsDrawerLinks role={user?.role} />
            {identityFooter(false)}
          </div>
        </div>
      )}

      {/* overflow-x-CLIP, never -hidden (Session AAX, Loop 3): hidden makes <main> the sticky
          containing scrollport, which silently broke every sticky descendant -- the top search
          header never actually pinned, and the costing totals bar could not either. clip keeps
          the same no-horizontal-scroll guarantee without creating a scroll container. */}
      {/* `inert` while the drawer is open (Session ACW review fix): the entire background --
          header, hamburger, search, page content -- leaves the tab order and the accessibility
          tree, which is what aria-modal="true" on the drawer promises. Removed the moment the
          drawer closes (and the cross-md effect guarantees it can never stick on a desktop
          viewport). */}
      <main inert={drawerOpen || undefined} className="min-w-0 flex-1 overflow-x-clip">
        {/* Session ACG locked header: a 48px #050816 band carrying the single brand mark
            (public/brand/ele-logo.png at 26px, left) and the global search (right, Cmd/Ctrl+K),
            finished with the 2px tri-colour gradient bottom rule (.ele-office-header::after).
            The band is SOLID (the shell token: #050816 dark, white light) so content never shows
            through the sticky band. Below md (Session ACW) the band grows to 56px (h-14) and
            gains the hamburger at the far left; the gradient rule is width-based so it spans the
            header at any size. */}
        <header data-app-header="" className="ele-office-header sticky top-0 z-20 flex h-14 items-center justify-between gap-4 bg-[var(--ele-shell)] px-4 md:h-12">
          {/* min-w-0 here and on the brand link and image (Session DOC1-TIDY): on a 320px phone with the text size at 150% the two
              clusters no longer fit, and a flex-none logo then ran under the search icon. Below md the logo now gives way first (it
              shrinks, never overlaps) and the right cluster is flex-none so it keeps its controls whole. From md up the three stay
              flex-none exactly as the locked header always had them (ACG: same mark, same 26px); there the search input narrows instead. */}
          <div className="flex min-w-0 items-center gap-1.5 md:flex-none">
            {/* Hamburger (mobile only): a 44x44 tap target in the true brand purple that opens
                the nav drawer -- below md there is no sidebar, so this is the only way into the
                nav. text-ele-purple stays #7d21c7 inside .office-ui (only the
                --ele-purple-accent hairline var is overridden there, never --ele-purple). */}
            <button
              ref={hamburgerRef}
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              className="-ml-2 flex h-11 w-11 flex-none items-center justify-center rounded-ele text-ele-purple md:hidden"
            >
              <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6 fill-current">
                <path d="M3 6h18v2H3zM3 11h18v2H3zM3 16h18v2H3z" />
              </svg>
            </button>
            {/* The brand mark goes home (Session AEA): same mark, same 26px, same position, now
                a link back to the dashboard (standard web behaviour). Purple focus ring only
                (the ACG focus convention); no hover wash on the locked header band. */}
            <Link
              to="/office"
              aria-label="Go to the dashboard"
              title="Go to the dashboard"
              className="flex min-w-0 items-center rounded-ele outline-none focus-visible:ring-2 focus-visible:ring-ele-purple max-md:min-h-11 md:flex-none"
              {...lockedLinkProps(navLocked)}
            >
              <img src="/brand/ele-logo.png" alt="East London Elevators" className="h-[26px] w-auto min-w-0 max-w-full object-contain object-left md:flex-none" />
            </Link>
          </div>
          {/* Right cluster: global search, the desktop-notification opt-in (Session AQS, item 22:
              moved here from Settings, previously buried on one admin-adjacent page) + the theme
              toggle (Session AFK) at the far right, the brief's prescribed position. */}
          <div className="flex min-w-0 items-center justify-end gap-1.5 max-md:flex-none">
            <GlobalSearch role={user?.role} />
            {/* Documents (Session DOC1): the Schedule of Fees, and for an admin the subcontractor rate card, two taps from any page. */}
            <DocumentsMenu role={user?.role} />
            <PushNotificationsButton />
            {/* Phone view (Session BDZ): an admin on a screen wider than 900px only. */}
            <PhoneViewButton />
            <ThemeToggle />
          </div>
        </header>
        {/* The content fills ALL remaining space: no fixed max-width cap, so when the sidebar
            collapses the content area expands to use the freed width (the flex-1 main tracks the
            sidebar's animated width). Session AJ, Item 6. 16px padding below md (Session ACW),
            the original 24px from md up. */}
        <div className="p-4 md:p-6">
          {hubStrip}
          <Outlet />
        </div>
        <IdleSessionWarning />
      </main>
      <PhoneViewHost />
    </div>
    </PinDragContext.Provider>
  );
}

// A draggable nav item (expanded sidebar). The NavLink stays clickable for navigation;
// a dedicated drag handle (a grip icon carrying the sortable listeners -- NOT the whole
// item) is what the Director drags. The handle is always visible (a clear grab
// affordance) and brightens on hover. Keyed by the item's `to` path (the stable id).
// Exported so the DOM presence of the handle can be asserted in a render test.
export function SortableNavItem({ item, badge }: { item: NavItem; badge?: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.to });
  const style = { transform: CSS.Transform.toString(transform), transition };
  // While the dashboard is customising (Session AEA), the LINK itself becomes a native pin-drag
  // source (drag it onto the pinned-shortcuts block). The dnd-kit reorder is untouched: its
  // listeners live only on the grip handle below, which is a sibling of the link, so
  // grip = reorder (always) and row = pin (customise mode only).
  const { customising } = usePinDrag();
  const navLocked = useIsNavigationLocked();
  const { pathname, search } = useLocation();
  const active = navItemActive(item, pathname, search);
  return (
    <li ref={setNodeRef} style={style} className={`group relative ${isDragging ? "z-10 opacity-80" : ""}`}>
      {/* pr-7 reserves space for the always-visible drag handle so it never overlaps the label
          text (the grip previously read as a trailing ":" over a truncated label); min-w-0 flex-1
          lets a long label truncate cleanly instead of pushing under the handle (Session AR
          Item 15). Combined with the wider w-64 sidebar, the current labels fit in full.
          Session AIB (mobile audit M4): below md the handle is not rendered at all, so the
          reservation is dropped back to the link's own px-2 there. It was never enough anyway --
          pr-7 is 28px against the handle's max-md:w-8 (32px) sitting 4px from the edge, so the
          handle overlapped the label's tap area by about 8px on a phone. */}
      <Link
        to={item.to}
        title={item.hint ?? item.label}
        aria-current={active ? "page" : undefined}
        className={`${navLinkClass(false)({ isActive: active })} max-md:pr-2 pr-7`}
        {...pinDragSourceProps(item, customising)}
        {...lockedLinkProps(navLocked)}
      >
        <NavIcon icon={item.icon} to={item.to} />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {badge ? (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-ele-green px-1.5 text-[10px] font-bold text-ele-bg" title={`${badge} active`}>
            {badge}
          </span>
        ) : null}
      </Link>
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${item.to}`}
        aria-label={`Drag to reorder ${item.label}`}
        title="Drag to reorder"
        /* Session AIB, mobile audit M4: HIDDEN below md. `touch-none` (touch-action: none) is what
           lets a touch start a drag rather than a scroll, so on a phone this handle was a 32px dead
           strip down the right edge of every nav item (56 since Session AIM removed Banners, 57 at
           AIB) -- exactly where a right-handed thumb rests -- in a drawer that must scroll (nine
           groups against an h-dvh panel). A swipe
           begun there was handed to PointerSensor, which arms a drag after 5px, instead of
           scrolling. Removing `touch-none` instead would NOT fix it: the handle would simply stop
           working and remain a dead strip.
           CSS-only, deliberately. dnd-kit's node ref is on the <li> (setNodeRef above), not on this
           button, so the item stays a registered sortable node with the button display:none: no
           dnd-kit warning, and a cross-group drop from a DESKTOP window still resolves onto it. A
           JS-conditional render would deregister nothing but WOULD turn five assertions red across
           four suites, OfficeLayoutDrawer.test.tsx among them, which asserts the handle is present
           inside the drawer as proof it shares `expandedNav` rather than forking it.
           Nothing is lost but the gesture: the sensor list is PointerSensor only (no
           KeyboardSensor), so there was no keyboard path to reorder on any viewport, and a custom
           order saved on a desktop still RENDERS on the phone. The group grip below is hidden with
           it, because leaving whole-group dragging live while item dragging is gone is half a
           feature, not a smaller one. */
        className="absolute right-1 top-1/2 flex h-6 w-5 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded text-ele-body opacity-50 transition group-hover:opacity-90 hover:!opacity-100 active:cursor-grabbing max-md:hidden"
      >
        <svg aria-hidden viewBox="0 0 10 16" className="h-3.5 w-3.5 fill-current">
          <circle cx="3" cy="3" r="1.3" />
          <circle cx="7" cy="3" r="1.3" />
          <circle cx="3" cy="8" r="1.3" />
          <circle cx="7" cy="8" r="1.3" />
          <circle cx="3" cy="13" r="1.3" />
          <circle cx="7" cy="13" r="1.3" />
        </svg>
      </button>
    </li>
  );
}

// A draggable nav GROUP (Session AD, Item 4). The whole group block is the sortable node (so the
// header and its items move together when reordered), but only a small grip handle on the heading
// carries the drag listeners; the heading label itself stays a click target that toggles the group.
// The items render inside their own SortableContext, so they reorder within the group AND can be
// dragged across to another group (resolved by applyNavDragEnd on drop). Keyed by the group label.
export function SortableGroup({
  section,
  open,
  onToggle,
  badges,
}: {
  section: NavSection;
  open: boolean;
  onToggle: () => void;
  badges: Record<string, number>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: navGroupSortId(section.label) });
  const style = { transform: CSS.Transform.toString(transform), transition };
  // D28's badge rollup: only surfaced while the group is CLOSED, since an open group already
  // shows each item's own badge (SortableNavItem) -- showing both would double-count the same
  // number on screen.
  const rollup = open ? 0 : groupBadgeTotal(section.items, badges);
  return (
    <div ref={setNodeRef} style={style} className={`mb-3 ${isDragging ? "z-20 opacity-80" : ""}`}>
      <div className="flex items-center gap-1 rounded px-2 pb-1 pt-0.5">
        {/* The heading label toggles the group. The active group is always open (the chevron
            reflects the rendered state). */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          data-testid={`nav-group-${section.label}`}
          className="flex flex-1 items-center justify-between gap-2 font-menu text-[9px] tracking-[0.16em] text-[var(--ele-nav-heading)] transition hover:text-ele-body"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate">{section.label}</span>
            {rollup > 0 ? (
              <span
                data-testid={`nav-group-badge-${section.label}`}
                title={`${rollup} active`}
                className="flex h-4 min-w-4 flex-none items-center justify-center rounded-full bg-ele-green px-1 text-[9px] font-bold text-ele-bg"
              >
                {rollup}
              </span>
            ) : null}
          </span>
          <GroupChevron open={open} />
        </button>
        {/* The grip moves the whole group. */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          data-testid={`group-drag-handle-${section.label}`}
          aria-label={`Drag to move the ${section.label} group`}
          title="Drag to move group"
          /* Hidden below md with the item handles (Session AIB, mobile audit M4). This one is in
             normal flow rather than absolute, so it did not overlap anything -- it inflated the
             heading row to 44px -- but it carries the same `touch-none`, so it is the same scroll
             trap on the heading rows, and hiding only half of drag-to-reorder would be worse than
             hiding it. */
          className="flex h-5 w-4 flex-none cursor-grab touch-none items-center justify-center rounded text-ele-body opacity-40 transition hover:opacity-90 active:cursor-grabbing max-md:hidden"
        >
          <svg aria-hidden viewBox="0 0 10 16" className="h-3 w-3 fill-current">
            <circle cx="3" cy="4" r="1.2" />
            <circle cx="7" cy="4" r="1.2" />
            <circle cx="3" cy="8" r="1.2" />
            <circle cx="7" cy="8" r="1.2" />
            <circle cx="3" cy="12" r="1.2" />
            <circle cx="7" cy="12" r="1.2" />
          </svg>
        </button>
      </div>
      {/* Items, clipped by a max-height transition so the group opens/closes smoothly. Kept in the
          DOM (not unmounted) so the drag-to-reorder sortable ids stay registered.
          Session AIB (mobile audit L1): the open value was 500px. A nav row is about 38px (a 24px
          icon chip + py-1.5, plus the ul's space-y-0.5), so 500px held 13 items and the largest
          group would have silently lost its last rows at 14 with no scrollbar and no visual cue.
          Latent rather than live: the largest group today is Compliance at 11 (about 416px). 640px
          holds 16. Only the OPEN branch moves; max-h-0 is what actually hides a closed group, since
          the items stay mounted. */}
      <div className={`overflow-hidden transition-[max-height] duration-200 ${open ? "max-h-[640px]" : "max-h-0"}`}>
        <SortableContext items={section.items.map((i) => i.to)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-0.5">
            {section.items.map((item) => (
              <SortableNavItem key={item.to} item={item} badge={navItemBadge(item, badges)} />
            ))}
          </ul>
        </SortableContext>
      </div>
    </div>
  );
}
